/**
 * Browser capability report.
 *
 * Lakaran LK leans on three browser features that are not universally implemented:
 *
 * - **WebGPU** — the only renderer.
 * - **OPFS** (`navigator.storage.getDirectory`) — the default project store.
 * - **File System Access** (`showDirectoryPicker`) — the optional linked folder on a PC or a
 *   mapped network drive. This one is Chromium only (Chrome/Edge/Opera); Gecko and WebKit have
 *   never shipped it, so folder linking is Edge/Chrome-only by design, not a bug.
 *
 * The point of this module is that a missing feature is *explained* rather than looking broken.
 */

export type BrowserFamily = 'chromium' | 'firefox' | 'safari' | 'unknown';

export interface BrowserInfo {
  family: BrowserFamily;
  name: string;
  version: number | null;
  /** Chromium engines (Edge, Chrome, Opera, Brave…) support the File System Access API. */
  folderCapable: boolean;
}

export function detectBrowser(ua: string = navigator.userAgent): BrowserInfo {
  const firefox = /Firefox\/(\d+(?:\.\d+)?)/.exec(ua);
  if (firefox) {
    return { family: 'firefox', name: 'Firefox', version: Number(firefox[1]), folderCapable: false };
  }
  const edge = /Edg\/(\d+(?:\.\d+)?)/.exec(ua);
  if (edge) return { family: 'chromium', name: 'Edge', version: Number(edge[1]), folderCapable: true };
  const opera = /OPR\/(\d+(?:\.\d+)?)/.exec(ua);
  if (opera) {
    return { family: 'chromium', name: 'Opera', version: Number(opera[1]), folderCapable: true };
  }
  const chrome = /(?:Chrome|Chromium)\/(\d+(?:\.\d+)?)/.exec(ua);
  if (chrome) {
    return { family: 'chromium', name: 'Chrome', version: Number(chrome[1]), folderCapable: true };
  }
  const safari = /Version\/(\d+(?:\.\d+)?).*Safari/.exec(ua);
  if (safari) {
    return { family: 'safari', name: 'Safari', version: Number(safari[1]), folderCapable: false };
  }
  return { family: 'unknown', name: 'unknown browser', version: null, folderCapable: false };
}

export interface Capabilities {
  browser: BrowserInfo;
  /** `navigator.gpu` exists at all. */
  webgpuApi: boolean;
  /** An adapter could actually be requested. */
  webgpuAdapter: boolean;
  opfs: boolean;
  folderLink: boolean;
  fileLaunch: boolean;
  persistence: boolean;
  secureContext: boolean;
}

type PickerHost = {
  showDirectoryPicker?: unknown;
  launchQueue?: unknown;
  gpu?: { requestAdapter(): Promise<unknown | null> };
};

export async function probeCapabilities(): Promise<Capabilities> {
  const host = window as unknown as PickerHost;
  let webgpuAdapter = false;
  if (host.gpu) {
    try {
      webgpuAdapter = (await host.gpu.requestAdapter()) != null;
    } catch {
      webgpuAdapter = false;
    }
  }

  const storage = navigator.storage as StorageManager & { getDirectory?: () => Promise<unknown> };
  return {
    browser: detectBrowser(),
    webgpuApi: !!host.gpu,
    webgpuAdapter,
    opfs: typeof storage?.getDirectory === 'function',
    folderLink: typeof host.showDirectoryPicker === 'function',
    fileLaunch: !!host.launchQueue,
    persistence: typeof storage?.persist === 'function',
    secureContext: window.isSecureContext,
  };
}

const yes = (flag: boolean): string => (flag ? 'yes' : 'NO');

/** One line per capability — used by the `BROWSER` command and bug reports. */
export function formatCapabilities(caps: Capabilities): string {
  const { browser } = caps;
  const version = browser.version === null ? '' : ` ${browser.version}`;
  return [
    `BROWSER  ${browser.name}${version}${caps.secureContext ? '' : ' (insecure context)'}`,
    `WebGPU   ${yes(caps.webgpuAdapter)}${caps.webgpuApi ? '' : ' — navigator.gpu missing'}`,
    `OPFS     ${yes(caps.opfs)} (browser storage — always available where Save works)`,
    `Folders  ${yes(caps.folderLink)} (linked PC / network folder — Chromium only)`,
    `Workers  ${yes(true)} · persistence ${yes(caps.persistence)} · file launch ${yes(caps.fileLaunch)}`,
  ].join('\n');
}

/** Why folder linking is unavailable here, in the user's terms. */
export function folderLinkExplanation(browser: BrowserInfo): string {
  if (browser.family === 'firefox') {
    return [
      `Folder linking needs the File System Access API, which Firefox does not implement (it is Chromium-only, so it works in Edge and Chrome).`,
      'Projects are saved in browser storage instead, and Export ▸ .lakar writes a file you can put on any folder or network drive.',
    ].join(' ');
  }
  if (browser.family === 'safari') {
    return [
      'Folder linking needs the File System Access API, which Safari does not implement (Chromium-only: Edge and Chrome).',
      'Projects are saved in browser storage instead; Export ▸ .lakar writes a file anywhere you like.',
    ].join(' ');
  }
  return [
    'Folder linking needs the File System Access API (`showDirectoryPicker`), which is missing here.',
    'That happens outside Chromium, but Chromium also hides it on an insecure origin — open Lakaran over `https://` or `http://localhost` rather than a plain `http://` LAN address, and use Edge or Chrome 86+.',
    'Meanwhile keep working in browser storage and Export ▸ .lakar for a file.',
  ].join(' ');
}

/** What to do when there is no usable WebGPU adapter, per browser. */
export function webGpuGuidance(browser: BrowserInfo): string {
  const common = 'Lakaran LK renders exclusively on WebGPU — there is no WebGL fallback.';
  if (browser.family === 'firefox') {
    const old = browser.version !== null && browser.version < 141;
    return [
      common,
      old
        ? `Firefox ${browser.version} only has WebGPU behind a flag: <code>about:config</code> → <code>dom.webgpu.enabled = true</code>, then restart.`
        : 'Firefox ships WebGPU on <b>Windows only</b> (141+); macOS on Apple silicon from 145, macOS on Intel and Linux are not supported yet. Check <code>about:support → Graphics</code> for a blocked driver.',
      'Folder linking is Chromium-only — in Firefox use browser storage plus <b>Export ▸ .lakar</b>.',
    ].join('<br />');
  }
  if (browser.family === 'safari') {
    return [
      common,
      'WebGPU needs Safari 26+; on older builds enable <i>Develop ▸ Feature Flags ▸ WebGPU</i>.',
      'Folder linking is Chromium-only — use browser storage plus <b>Export ▸ .lakar</b>.',
    ].join('<br />');
  }
  if (browser.family === 'chromium') {
    return [
      common,
      'No adapter is exposed: update to Chrome/Edge 113+, make sure hardware acceleration is on, and check <code>chrome://gpu</code> — a blocklisted driver disables WebGPU as well.',
    ].join('<br />');
  }
  return `${common}<br />Use a current Chrome or Edge (113+) with GPU acceleration enabled.`;
}
